import { ProcessingOverlay } from '@components'

import { BorderRadius, Color, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'

import { BankTransferFields, PaymentMethodsTextField } from '../../../../../PaymentMethodsFields'
import { usePaymentMethodsForm } from '../../../../context/PaymentMethodsFormContext'
import { ActivateStripeCardPayment } from './elements/ActivateStripeCardPayment'
import { PaymentMethodsAccordion, PaymentMethodsAccordionProps } from './elements/PaymentMethodsAccordion'
import * as Styled from './styles'

export const ModalBody = (): ReactElement => {
  const { t } = useTranslation()
  const { isProcessing, isLoading, paymentMethodsForm } = usePaymentMethodsForm()
  const { getValues, setValue } = paymentMethodsForm

  const { isConnected: isStripeConnected, isLoading: isStripeConnectedLoading } = usePayNowStatus()
  const shouldDisplayCardPaymentEntry = !isStripeConnectedLoading && !isStripeConnected

  const [bank, iban, offlinePayment, paypalEmail, swift] = getValues([
    'bank',
    'iban',
    'offlinePayment',
    'paypalEmail',
    'swift',
  ])

  const handleBankDelete = useCallback(() => {
    setValue('bank', '')
    setValue('iban', '')
    setValue('swift', '')
  }, [setValue])

  const paymentMethods: PaymentMethodsAccordionProps[] = useMemo(
    () => [
      {
        children: (
          <BankTransferFields
            fields={{
              bank: { label: t('external_invoices.payment_methods_modal.bank.label') },
              iban: { label: t('external_invoices.payment_methods_modal.iban.label') },
              swift: { label: t('external_invoices.payment_methods_modal.swift.label') },
            }}
            size="sm"
          />
        ),
        icon: 'bank',
        initialIsOpen: [bank, iban, swift].some(Boolean),
        onDelete: handleBankDelete,
        title: t('external_invoices.payment_methods_modal.method.bank_transfer.title'),
      },
      ...(shouldDisplayCardPaymentEntry
        ? [
            {
              badgeLabel: t('external_invoices.payment_methods_modal.method.card_payment.new_badge'),
              children: <ActivateStripeCardPayment />,
              icon: 'creditCard' as const,
              initialIsOpen: false,
              title: t('external_invoices.payment_methods_modal.method.card_payment.title'),
            },
          ]
        : []),
      {
        children: (
          <PaymentMethodsTextField
            fieldKey="paypalEmail"
            placeholder={t('external_invoices.payment_methods_modal.paypal.placeholder')}
            size="sm"
          />
        ),
        icon: 'paypal',
        initialIsOpen: !!paypalEmail,
        onDelete: () => setValue('paypalEmail', ''),
        title: t('external_invoices.payment_methods_modal.method.paypal.title'),
      },
      {
        children: (
          <PaymentMethodsTextField
            fieldKey="offlinePayment"
            placeholder={t('external_invoices.payment_methods_modal.offline_payment.placeholder')}
            size="sm"
          />
        ),
        icon: 'banknote',
        initialIsOpen: !!offlinePayment,
        onDelete: () => setValue('offlinePayment', ''),
        title: t('external_invoices.payment_methods_modal.method.offline_payment.title'),
      },
    ],
    [bank, handleBankDelete, iban, offlinePayment, paypalEmail, setValue, shouldDisplayCardPaymentEntry, swift, t],
  )

  return (
    <Styled.ModalBodyWrapper>
      {(isProcessing || isLoading) && (
        <ProcessingOverlay color={Color.White} borderRadius={BorderRadius.R16} withAnimation />
      )}
      <Text>{t('external_invoices.payment_methods_modal.description')}</Text>
      <Space size="s24" />
      {paymentMethods.map((method) => (
        <PaymentMethodsAccordion
          badgeLabel={method.badgeLabel}
          icon={method.icon}
          initialIsOpen={method.initialIsOpen}
          key={method.title}
          onDelete={method.onDelete}
          title={method.title}
        >
          {method.children}
        </PaymentMethodsAccordion>
      ))}
    </Styled.ModalBodyWrapper>
  )
}
